'use client';

import { useEffect, useId, useMemo, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { Dropdown, DropdownOption } from '../../atoms/Dropdown/Dropdown';
import { Separator } from '../../atoms/Separator/Separator';
import { RichText } from '../../molecules/RichText/RichText';
import { MyAssetDetail } from '../MyAssetDetail/MyAssetDetail';
import {
  FILTER_BY_RESULT_DEFAULT,
  FilterByResult,
  MyAssetsListProps,
  SORT_BY_DEFAULT,
  SortByOption,
} from './MyAssetsList.types';

const SortByDropdown = ({
  microcopy,
  onSortBy,
  ariaLabelledBy,
}: Pick<MyAssetsListProps, 'microcopy' | 'onSortBy'> & {
  ariaLabelledBy: string;
}) => {
  const [sortByPurchasedDateMicrocopy, setSortByPurchasedDateMicrocopy] =
    useState<string>(microcopy.sortByPurchasedDate);
  const [sortByNameAscMicrocopy, setSortByNameAscMicrocopy] = useState<string>(
    microcopy.sortByNameAsc
  );
  const [sortByNameDescMicrocopy, setSortByNameDescMicrocopy] =
    useState<string>(microcopy.sortByNameDesc);

  const sortByOptions = useMemo(
    () => [
      {
        value: SortByOption.PURCHASED_DATE,
        label: sortByPurchasedDateMicrocopy,
      },
      {
        value: SortByOption.NAME_ASC,
        label: sortByNameAscMicrocopy,
      },
      {
        value: SortByOption.NAME_DESC,
        label: sortByNameDescMicrocopy,
      },
    ],
    [
      sortByPurchasedDateMicrocopy,
      sortByNameAscMicrocopy,
      sortByNameDescMicrocopy,
    ]
  );

  const [selectedSortBy, setSelectedSortBy] = useState<DropdownOption>(
    sortByOptions.find((option) => option.value === SORT_BY_DEFAULT) ||
      sortByOptions[0]
  );

  useEffect(() => {
    setSortByPurchasedDateMicrocopy(microcopy.sortByPurchasedDate);
    setSortByNameAscMicrocopy(microcopy.sortByNameAsc);
    setSortByNameDescMicrocopy(microcopy.sortByNameDesc);
  }, [
    microcopy.sortByPurchasedDate,
    microcopy.sortByNameAsc,
    microcopy.sortByNameDesc,
  ]);

  return (
    <Dropdown
      id="my-assets-sort-by-dropdown"
      options={sortByOptions}
      selectedOption={selectedSortBy}
      ariaLabelledBy={ariaLabelledBy}
      onSelect={(opt) => {
        setSelectedSortBy(opt);
        onSortBy(opt.value as SortByOption);
      }}
      isDarkTheme={false}
      className="lg:min-w-36 lg:max-w-fit"
    />
  );
};

const ResultsDropdown = ({
  onFilterByResult,
  ariaLabelledBy,
}: Pick<MyAssetsListProps, 'onFilterByResult'> & {
  ariaLabelledBy: string;
}) => {
  const sortByOptions: DropdownOption[] = [
    {
      value: String(FilterByResult.SMALL),
      label: String(FilterByResult.SMALL),
    },
    {
      value: String(FilterByResult.MEDIUM),
      label: String(FilterByResult.MEDIUM),
    },
    {
      value: String(FilterByResult.LARGE),
      label: String(FilterByResult.LARGE),
    },
  ];

  const [selectedSortBy, setSelectedSortBy] = useState<DropdownOption>(
    sortByOptions.find(
      (option) => option.value === String(FILTER_BY_RESULT_DEFAULT)
    ) || sortByOptions[0]
  );

  return (
    <Dropdown
      id="my-assets-results-dropdown"
      options={sortByOptions}
      selectedOption={selectedSortBy}
      ariaLabelledBy={ariaLabelledBy}
      onSelect={(opt) => {
        setSelectedSortBy(opt);
        onFilterByResult(Number(opt.value) as FilterByResult);
      }}
      isDarkTheme={false}
      className="w-fit min-w-20"
    />
  );
};

export const MyAssetsList = ({
  results,
  assets,
  categories,
  selectedCategory,
  microcopy,
  className = '',
  variant = 'default',
  onSortBy,
  onFilterByResult,
  onFilterByCategory,
}: MyAssetsListProps) => {
  const dropdownLabelIdPrefix = useId().replace(/:/g, '');
  const sortByLabelId = `${dropdownLabelIdPrefix}-my-assets-sort-by-label`;
  const resultsPerPageLabelId = `${dropdownLabelIdPrefix}-my-assets-results-per-page-label`;
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  return (
    <div
      data-test="upm-packages-section"
      className={c('flex flex-col gap-11', className)}
    >
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
      <h1 className="hidden text-5xl font-bold text-content lg:block">
        {microcopy.title}
      </h1>
      <div className="flex gap-2">
        <nav className="hidden w-72 lg:block">
          <div className="pb-5">
            <span className="text-sm font-semibold uppercase tracking-wide text-content-muted">
              {microcopy.categories}
            </span>
          </div>
          <div className="flex flex-col items-start">
            {categories.map((category) => (
              <button
                key={category.label}
                aria-label={category.label}
                aria-pressed={selectedCategory === category.label}
                className="w-full space-x-1 py-2 text-left hover:bg-surface-muted"
                onClick={() => {
                  const isDeselecting = selectedCategory === category.label;
                  onFilterByCategory(isDeselecting ? '' : category.label);
                  const categorySelectedAnnouncement =
                    microcopy.categorySelected
                      ? interpolateStringWithData(microcopy.categorySelected, {
                          category: category.label,
                        })
                      : `${category.label} selected`;
                  setLiveAnnouncement(
                    isDeselecting
                      ? (microcopy.categoryDeselected ??
                          `${category.label} deselected`)
                      : categorySelectedAnnouncement
                  );
                }}
              >
                <span
                  className={c(
                    'text-base text-content',
                    selectedCategory === category.label && 'font-bold'
                  )}
                >
                  {category.label}
                </span>
                <span className="text-base text-content-subtle">
                  ({category.count})
                </span>
              </button>
            ))}
          </div>
        </nav>

        <div className="flex w-full flex-col gap-6">
          <div className="flex justify-between gap-2">
            <div className="w-full space-y-2">
              <div id={sortByLabelId} className="text-sm text-content-subtle">
                {microcopy.sortBy}
              </div>
              <SortByDropdown
                microcopy={microcopy}
                onSortBy={onSortBy}
                ariaLabelledBy={sortByLabelId}
              />
            </div>
            <div className="shrink-0 space-y-2">
              <div
                id={resultsPerPageLabelId}
                className="text-sm text-content-subtle"
              >
                {microcopy.resultsPerPage}
              </div>
              <ResultsDropdown
                onFilterByResult={onFilterByResult}
                ariaLabelledBy={resultsPerPageLabelId}
              />
            </div>
          </div>
          <div>
            <div>
              <div className="flex">
                <RichText
                  html={results}
                  className="text-sm text-content-subtle lg:w-[422px]"
                />
                {variant === 'show-update' && (
                  <div className="hidden text-sm text-content-subtle lg:block">
                    {microcopy.update}
                  </div>
                )}
              </div>
              <Separator />
              {assets.length > 0 ? (
                assets.map((asset) => (
                  <div key={asset.productId}>
                    <MyAssetDetail {...asset} microcopy={microcopy.detail} />
                    <Separator />
                  </div>
                ))
              ) : (
                <div className="text-sm text-content-muted">
                  {microcopy.noResults}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
